import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUserLeagues, getLeagueTable } from "../services/leagueService";
import { getCurrentGameweek, getPredictionsForGameweek } from "../services/predictionService";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import AlertMessage from "../components/AlertMessage";

function GameweekBanner({ gameweek }) {
  if (!gameweek) return null;

  const isLocked = gameweek.isLocked;
  const lockDate = gameweek.lockUtc ? new Date(gameweek.lockUtc) : null;
  const formattedLock = lockDate
    ? lockDate.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className={`rounded-3xl p-4 sm:p-6 ${isLocked ? "bg-slate-800" : "bg-gradient-to-r from-emerald-600 to-emerald-500"}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
        Current Gameweek
      </p>
      <h2 className="mt-1 text-xl sm:text-2xl font-bold text-white">
        {gameweek.name || `Gameweek ${gameweek.number}`}
      </h2>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 sm:px-3 py-1 text-xs font-semibold ${isLocked ? "bg-red-500/20 text-red-300" : "bg-white/20 text-white"}`}>
          {isLocked ? "Locked" : "Open for picks"}
        </span>
        {formattedLock && !isLocked && (
          <span className="text-xs text-white/70">Locks {formattedLock}</span>
        )}
      </div>
    </div>
  );
}

function LeagueDashboardCard({ league, userId, gameweekId }) {
  const [table, setTable] = useState(null);
  const [picksProgress, setPicksProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !league?.id || !gameweekId) return;

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      try {
        const [tableData, predictions] = await Promise.all([
          getLeagueTable(league.id, { signal: controller.signal }),
          getPredictionsForGameweek(userId, league.id, gameweekId, { signal: controller.signal }),
        ]);

        setTable(tableData);

        const saved = predictions.filter(
          (p) => p.predictedHomeScore != null && p.predictedAwayScore != null
        ).length;
        setPicksProgress({ saved, total: predictions.length });
      } catch (error) {
        if (error?.name === "AbortError") return;
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => controller.abort();
  }, [userId, league?.id, gameweekId]);

  const rows = Array.isArray(table?.rows)
    ? [...table.rows].sort((a, b) => b.totalPoints - a.totalPoints).slice(0, 3)
    : [];

  const allPicked = picksProgress && picksProgress.total > 0 && picksProgress.saved === picksProgress.total;
  const nonePicked = picksProgress && picksProgress.saved === 0;

  return (
    <div className="flex flex-col rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 sm:p-5 pb-3 sm:pb-4">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold tracking-tight text-slate-900 truncate">{league.name}</h3>
          <span className="mt-1 inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
            {league.inviteCode}
          </span>
        </div>
        {picksProgress && picksProgress.total > 0 && (
          <span className={`shrink-0 rounded-full px-2.5 sm:px-3 py-1 text-xs font-semibold whitespace-nowrap ${allPicked ? "bg-emerald-100 text-emerald-700" : nonePicked ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
            {picksProgress.saved}/{picksProgress.total}
          </span>
        )}
      </div>

      <div className="flex-1 px-4 sm:px-5">
        {loading ? (
          <div className="space-y-2 pb-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="pb-4 text-xs sm:text-sm text-slate-400">No standings yet.</p>
        ) : (
          <ol className="space-y-1.5 pb-4">
            {rows.map((row, idx) => (
              <li key={row.userId} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${idx === 0 ? "bg-amber-400 text-amber-900" : "bg-slate-200 text-slate-600"}`}>
                    {idx + 1}
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-slate-800 truncate">{row.displayName}</span>
                </div>
                <span className="shrink-0 ml-2 text-xs sm:text-sm font-semibold text-slate-700">{row.totalPoints} pts</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-3 sm:p-4">
        <Link
          to={`/leagues/${league.id}/picks`}
          className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white transition hover:bg-emerald-700"
        >
          Picks
        </Link>
        <Link
          to={`/leagues/${league.id}/table`}
          className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          Table
        </Link>
      </div>
    </div>
  );
}

function DashboardPage({ userId }) {
  const hasValidUserId = Number.isInteger(userId) && userId > 0;
  const [leagues, setLeagues] = useState([]);
  const [gameweek, setGameweek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showWhatsNew, setShowWhatsNew] = useState(true);

  useEffect(() => {
    if (!hasValidUserId) {
      return;
    }

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setMessage("");

      try {
        const leaguesData = await getUserLeagues(userId, { signal: controller.signal });
        setLeagues(Array.isArray(leaguesData) ? leaguesData : []);

        // Get current gameweek for the first league (if available)
        if (Array.isArray(leaguesData) && leaguesData.length > 0) {
          const firstLeagueId = leaguesData[0].id;
          const gw = await getCurrentGameweek(firstLeagueId, { signal: controller.signal });
          setGameweek(gw);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        setMessage("Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => controller.abort();
  }, [userId, hasValidUserId]);

  if (!hasValidUserId || loading) {
    return (
      <div className="space-y-8">
        <div className="h-24 sm:h-32 animate-pulse rounded-3xl bg-slate-200" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-56 animate-pulse rounded-3xl bg-white shadow-sm ring-1 ring-slate-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Dashboard"
        title="Welcome back"
        subtitle="Here's a snapshot of your leagues and the current gameweek."
        dark
      />

      <AlertMessage message={message} type="error" />

      <GameweekBanner gameweek={gameweek} />

      <section>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-semibold tracking-tight">Your Leagues</h3>
            <p className="mt-1 text-xs sm:text-sm text-slate-500">
              Pick scores and track standings across all your leagues.
            </p>
          </div>
          <Link
            to="/leagues"
            className="shrink-0 rounded-full border border-slate-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 transition hover:bg-slate-50 text-center"
          >
            Manage leagues
          </Link>
        </div>

        {leagues.length === 0 ? (
          <EmptyState
            title="No leagues yet"
            description="Head to Manage leagues to create one or join with an invite code."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {leagues.map((league) => (
              <LeagueDashboardCard
                key={league.id}
                league={league}
                userId={userId}
                gameweekId={gameweek?.id}
              />
            ))}
          </div>
        )}
      </section>
      {showWhatsNew && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900">
                ✨ What's New?
              </h3>
              <button
                onClick={() => setShowWhatsNew(false)}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4 mb-6">
              <div className="flex gap-2 sm:gap-3">
                <span className="text-xl sm:text-2xl flex-shrink-0">📧</span>
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-slate-900">Pick Reminder Emails</h4>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">
                    Get automatic email reminders 4 hours before gameweek lock! Never miss your picks again. Customize when you want to be reminded in Settings.
                  </p>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3 sm:pt-4">
                <div className="flex gap-2 sm:gap-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">⚙️</span>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-slate-900">Notification Settings</h4>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      Control your email reminders in the new Settings page. Toggle reminders on/off and customize how many hours before lock you want to be reminded.
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3 sm:pt-4">
                <div className="flex gap-2 sm:gap-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">🔄</span>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-slate-900">Keep-Alive System</h4>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      The backend now stays warm and responsive. Your reminders will arrive reliably even during quiet periods. No action needed - it works automatically!
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3 sm:pt-4">
                <div className="flex gap-2 sm:gap-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">🎛️</span>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-slate-900">Admin: Manual Email Trigger</h4>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      Admins can now manually send pick reminders for any gameweek from the Admin Panel. Perfect for testing and special situations!
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowWhatsNew(false)}
              className="w-full inline-flex items-center justify-center rounded-2xl bg-blue-600 px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Got it!
            </button>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                Found an issue or have a feature suggestion?{" "}
                <a
                  href="mailto:sami@samiapp.uk"
                  className="font-medium text-blue-600 hover:text-blue-700 transition"
                >
                  Email us
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
